/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as THREE from 'three'
import { ThreeComponet } from './types'
import { MathEx } from '@ykob/js-util'
import vertexShader from './glsl/particles.vs'
import fragmentShader from './glsl/particles.fs'

interface ParticlesObjectUniforms {
  time: THREE.IUniform<number>
  [uniform: string]: THREE.IUniform<any>
}

interface ParticlesOptions {
  count: number
  size: number
  width: number
  height: number
  scaleMin: number
  minFloatHeight: number
  maxFloatHeight: number
  floatStartZRange: number
  insideColor: THREE.Color
  outsideColor: THREE.Color
  canvasHeight: number
}

export default class Particles extends ThreeComponet<ParticlesObjectUniforms> {
  constructor(private readonly options: ParticlesOptions) {
    super(
      {
        time: {
          // type: 'f',
          value: 0,
        },
      },
      false
    )
    this.obj = this.createObj()
  }

  // loadTexture(callback?: () => void): void {
  //   const loader = new THREE.TextureLoader()
  //   loader.load(logoImg, (texture) => {
  //     texture.magFilter = THREE.NearestFilter
  //     texture.minFilter = THREE.NearestFilter
  //     this.uniforms.texture.value = texture
  //     this.obj = this.createObj()
  //     this.isLoaded = true
  //     if (callback) callback()
  //   })
  // }

  createGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry()
    const {
      count,
      width,
      height,
      scaleMin,
      insideColor,
      outsideColor,
      minFloatHeight,
      maxFloatHeight,
      floatStartZRange,
    } = this.options
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const scales = new Float32Array(count)
    const floatStartPercents = new Float32Array(count)
    const floatStartZs = new Float32Array(count)
    const floatHeights = new Float32Array(count)

    const halfWidth = width / 2
    const halfHeight = height / 2
    const scaleRange = 1 - scaleMin
    const floatRange = maxFloatHeight - minFloatHeight

    for (let i = 0; i < count; i++) {
      const i3 = i * 3

      // position
      positions[i3] = Math.random() * width - halfWidth
      positions[i3 + 1] = Math.random() * height - halfHeight
      positions[i3 + 2] = 0

      // color
      const mixedColor = insideColor.clone()
      mixedColor.lerp(outsideColor, Math.random())

      colors[i3] = mixedColor.r
      colors[i3 + 1] = mixedColor.g
      colors[i3 + 2] = mixedColor.b

      // scales
      scales[i] = scaleMin + Math.random() * scaleRange
      // float
      floatStartPercents[i] = Math.random()
      floatStartZs[i] = Math.random() * floatStartZRange
      floatHeights[i] = minFloatHeight + Math.random() * floatRange
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    geometry.setAttribute(
      'aFloatStartPercents',
      new THREE.BufferAttribute(floatStartPercents, 1)
    )
    geometry.setAttribute(
      'aFloatStartZ',
      new THREE.BufferAttribute(floatStartZs, 1)
    )
    geometry.setAttribute(
      'aFloatHeights',
      new THREE.BufferAttribute(floatHeights, 1)
    )

    return geometry
  }

  createMaterial(): THREE.RawShaderMaterial {
    const { size, canvasHeight } = this.options
    const material = new THREE.RawShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        // 时间
        time: this.uniforms.time,
        // 这里需要强调，有些GPU不会显示size为0的点，根据pixel ratio，如果是2，那么1个单位的点是0.5片元，就不会显示了。
        uSize: { value: size * Math.min(window.devicePixelRatio, 2) },
        uCanvasHeight: { value: canvasHeight },
      },
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
    })
    return material
  }

  createObj(): THREE.Points {
    const mesh = new THREE.Points(this.createGeometry(), this.createMaterial())
    mesh.position.set(0, -250, 0)
    mesh.rotation.set(MathEx.radians(-90), 0, 0)
    return mesh
  }

  render(time: number): void {
    this.uniforms.time.value += time
  }
}
